import { useCallback, useEffect, useState } from "react";
import { useParams, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, setAccessKey } from "@/lib/api";
import { AppCtx } from "@/context/AppCtx";
import PublicPage from "@/pages/PublicPage";
import { Sidebar, MobileBar } from "@/components/Sidebar";

// Verifica la ruta secreta; si no coincide muestra la página pública neutra
export default function SystemShell() {
  const { slug } = useParams();
  const [state, setState] = useState("checking");
  const [settings, setSettings] = useState(null);

  const reload = useCallback(async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
    return data;
  }, []);

  useEffect(() => {
    let alive = true;
    setState("checking");
    // Reintenta si el servidor aún está arrancando; solo un "ok: false" explícito deniega
    const check = async (attempt = 0) => {
      try {
        const { data } = await api.get("/access/check", { params: { path: slug } });
        if (!alive) return;
        if (!data?.ok) return setState("denied");
        setAccessKey(slug);
        await reload();
        if (alive) setState("ok");
      } catch {
        if (!alive) return;
        if (attempt < 4) setTimeout(() => check(attempt + 1), 1500 * (attempt + 1));
        else setState("denied");
      }
    };
    check();
    return () => {
      alive = false;
    };
  }, [slug, reload]);

  useEffect(() => {
    if (settings) document.title = `${settings.company_name} · Sistema`;
  }, [settings]);

  if (state === "checking")
    return (
      <div className="min-h-screen app-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  if (state === "denied") return <PublicPage />;

  return (
    <AppCtx.Provider value={{ settings, setSettings, reload, base: `/${slug}` }}>
      <div className="min-h-screen app-bg text-slate-200">
        <Sidebar />
        <div className="lg:pl-64">
          <MobileBar />
          <main className="px-4 py-6 sm:px-6 lg:px-10 lg:py-10 max-w-[1500px]" data-testid="main-content">
            <Outlet />
          </main>
        </div>
      </div>
    </AppCtx.Provider>
  );
}
